import { useMemo, useState } from 'react';
import { useEditor } from '../../state/store';
import { Attr, colorToHex, TRANSPARENT } from '../../core/types';
import { charName } from '../../unicode/charsets';
import { encodeAnsi } from '../../ansi/Encoder';
import { escapeForDisplay } from '../../ansi/SGR';
import { estimateBandwidth, formatBytes, optimizeAnsi } from '../../ansi/Optimizer';
import { TERMINAL_PROFILES, checkCompatibility } from '../../analysis/compatibility';
import { Panel } from '../widgets/common';
import { Select } from '../widgets/primitives';

/** Cell inspector: what is actually under the cursor, and what it exports as. */
export function InspectorPanel(): JSX.Element {
  const cursor = useEditor((s) => s.cursor);
  const revision = useEditor((s) => s.revision);
  const tab = useEditor.getState().active();
  const selectionSize = tab.selection.size;

  const cell = cursor ? tab.doc.composite().get(cursor.x, cursor.y) : null;
  const attrs = cell
    ? Object.entries(Attr)
        .filter(([, bit]) => bit !== 0 && (cell.attrs & (bit as number)) !== 0)
        .map(([name]) => name.toLowerCase())
    : [];

  return (
    <Panel title="Inspector" defaultOpen={false}>
      <div key={revision}>
        <Row label="Position" value={cursor ? `${cursor.x}, ${cursor.y}` : '—'} />
        <Row label="Character" value={cell?.char ? `${cell.char}  ${charName(cell.char)}` : 'empty'} />
        <Row
          label="Foreground"
          value={cell ? (cell.fg === TRANSPARENT ? 'default' : colorToHex(cell.fg)) : '—'}
          swatch={cell && cell.fg !== TRANSPARENT ? colorToHex(cell.fg) : undefined}
        />
        <Row
          label="Background"
          value={cell ? (cell.bg === TRANSPARENT ? 'default' : colorToHex(cell.bg)) : '—'}
          swatch={cell && cell.bg !== TRANSPARENT ? colorToHex(cell.bg) : undefined}
        />
        <Row label="Attributes" value={attrs.length ? attrs.join(', ') : 'none'} />
        <Row label="Selection" value={selectionSize ? `${selectionSize} cells` : 'none'} />
        <Row label="Layer" value={tab.doc.activeLayer.name} />
      </div>
    </Panel>
  );
}

function Row({ label, value, swatch }: { label: string; value: string; swatch?: string }): JSX.Element {
  return (
    <div className="row" style={{ height: 20 }}>
      <span className="faint" style={{ width: 84 }}>
        {label}
      </span>
      {swatch ? (
        <span
          style={{
            width: 11,
            height: 11,
            borderRadius: 2,
            background: swatch,
            border: '1px solid var(--border-strong)',
          }}
        />
      ) : null}
      <span className="mono dim" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {value}
      </span>
    </div>
  );
}

/** Live view of the escape sequences the current document exports as. */
export function AnsiInspectorPanel(): JSX.Element {
  const revision = useEditor((s) => s.revision);
  const [rows, setRows] = useState(6);

  const { display, stats, optimized } = useMemo(() => {
    const tab = useEditor.getState().active();
    const grid = tab.doc.composite();
    const slice = grid.extract({ x: 0, y: 0, width: grid.width, height: Math.min(rows, grid.height) });
    const encoded = encodeAnsi(slice, {
      colorMode: tab.doc.colorMode,
      background: tab.doc.background,
    });
    const full = encodeAnsi(grid, { colorMode: tab.doc.colorMode, background: tab.doc.background });
    return {
      display: escapeForDisplay(encoded.text),
      stats: full.stats,
      optimized: optimizeAnsi(full.text).stats,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision, rows]);

  return (
    <Panel title="ANSI output" defaultOpen={false}>
      <div className="row" style={{ marginBottom: 6 }}>
        <span className="faint">First</span>
        <input
          className="input"
          type="number"
          min={1}
          max={40}
          value={rows}
          style={{ width: 56 }}
          onChange={(e) => setRows(Math.max(1, Math.min(40, Number(e.target.value) || 1)))}
        />
        <span className="faint">rows</span>
      </div>
      <pre
        className="terminal-preview"
        style={{ maxHeight: 150, fontSize: 10, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
      >
        {display}
      </pre>
      <div style={{ marginTop: 6 }}>
        <Row label="Encoded" value={formatBytes(stats.bytes)} />
        <Row label="Per-cell SGR" value={formatBytes(stats.naiveBytes)} />
        <Row label="Saved" value={`${(stats.savedRatio * 100).toFixed(1)}%`} />
        <Row label="Escapes" value={String(stats.escapeSequences)} />
        <Row
          label="Post-optimise"
          value={`${formatBytes(optimized.optimizedBytes)} (−${optimized.savedBytes} B)`}
        />
        <Row label="At 10 FPS" value={`${formatBytes(estimateBandwidth(stats.bytes, 10).bytesPerSecond)}/s`} />
      </div>
    </Panel>
  );
}

/** Per-profile compatibility report. */
export function CompatibilityPanel(): JSX.Element {
  const revision = useEditor((s) => s.revision);
  const [profileId, setProfileId] = useState(TERMINAL_PROFILES[0].id);
  const profile = TERMINAL_PROFILES.find((p) => p.id === profileId) ?? TERMINAL_PROFILES[0];

  const issues = useMemo(
    () => checkCompatibility(useEditor.getState().active().doc, profile),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [revision, profileId],
  );

  return (
    <Panel title="Compatibility" defaultOpen={false}>
      <div style={{ marginBottom: 6 }}>
        <Select
          ariaLabel="Terminal profile"
          value={profileId}
          options={TERMINAL_PROFILES.map((p) => ({
            value: p.id,
            label: p.name,
            hint: `${p.width}×${p.height}`,
          }))}
          onChange={setProfileId}
        />
      </div>
      <p className="faint" style={{ margin: '0 0 8px', lineHeight: 1.45 }}>
        {profile.note}
      </p>
      {issues.map((issue, i) => (
        <div key={i} style={{ marginBottom: 8 }}>
          <div
            className={
              issue.severity === 'error' ? 'error-text' : issue.severity === 'warning' ? 'warn-text' : 'ok-text'
            }
          >
            {issue.severity === 'error' ? '✕' : issue.severity === 'warning' ? '⚠' : '✓'} {issue.title}
          </div>
          <div className="faint" style={{ lineHeight: 1.45 }}>
            {issue.detail}
            {issue.example ? ` First at ${issue.example.x},${issue.example.y}.` : ''}
          </div>
        </div>
      ))}
    </Panel>
  );
}
